/**
 * Data layer for the `affiliate-approval` feature.
 *
 * Thin typed fetch wrappers over /api/v1/affiliate-approval. Raw `fetch()` is
 * confined to this file per the dash contract.
 *
 * Backend endpoints:
 *   GET  /api/v1/affiliate-approval/queue            — list applicants
 *   POST /api/v1/affiliate-approval/:id/approve      — approve one
 *   POST /api/v1/affiliate-approval/:id/reject       — reject one (reason req)
 *   POST /api/v1/affiliate-approval/bulk             — bulk approve/reject
 */

export const QUEUE_FILTERS = ["pending", "approved", "rejected", "all"] as const;
export type QueueFilter = (typeof QUEUE_FILTERS)[number];

export function isQueueFilter(v: unknown): v is QueueFilter {
	return typeof v === "string" && (QUEUE_FILTERS as readonly string[]).includes(v);
}

export interface AffiliateApprovalRow {
	id: string;
	tenant_id: string;
	email: string;
	name: string;
	company: string | null;
	country: string | null;
	traffic_source: string | null;
	status: "pending" | "approved" | "rejected" | "suspended";
	created_at: number;
	reviewed_by: string | null;
	reviewed_at: number | null;
	rejection_reason: string | null;
}

export interface QueueFiltersPayload {
	status: QueueFilter;
	country: string | null;
	traffic_source: string | null;
	limit: number;
}

export interface ListAffiliateApprovalResponse {
	count: number;
	filters: QueueFiltersPayload;
	items: AffiliateApprovalRow[];
}

export interface ApproveResponse {
	id: string;
	status: "approved";
	reviewed_by: string | null;
	reviewed_at: number;
}

export interface RejectResponse {
	id: string;
	status: "rejected";
	reviewed_by: string | null;
	reviewed_at: number;
	rejection_reason: string;
}

export interface ListQuery {
	status?: QueueFilter;
	country?: string | null;
	traffic_source?: string | null;
	limit?: number;
}

const BASE = "/api/v1/affiliate-approval";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

function buildQueueUrl(q: ListQuery): string {
	const params = new URLSearchParams();
	if (q.status) params.set("status", q.status);
	if (q.country) params.set("country", q.country);
	if (q.traffic_source) params.set("traffic_source", q.traffic_source);
	if (q.limit && Number.isFinite(q.limit)) params.set("limit", String(q.limit));
	const qs = params.toString();
	return `${BASE}/queue${qs ? `?${qs}` : ""}`;
}

export async function fetchQueue(
	query: ListQuery = {},
): Promise<ListAffiliateApprovalResponse> {
	const res = await fetch(buildQueueUrl(query), {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListAffiliateApprovalResponse>(res);
}

/**
 * Thin "list" alias expected by the scaffold shape. Uses the default queue
 * filter (pending). Page components call `fetchQueue` directly with filters.
 */
export async function list(): Promise<ListAffiliateApprovalResponse> {
	return fetchQueue();
}

export async function approve(id: string): Promise<ApproveResponse> {
	const res = await fetch(
		`${BASE}/${encodeURIComponent(id)}/approve`,
		{
			method: "POST",
			credentials: "include",
			headers: {
				accept: "application/json",
				"content-type": "application/json",
			},
			body: JSON.stringify({}),
		},
	);
	return parseOrThrow<ApproveResponse>(res);
}

export async function reject(
	args: { id: string; reason: string },
): Promise<RejectResponse> {
	const res = await fetch(
		`${BASE}/${encodeURIComponent(args.id)}/reject`,
		{
			method: "POST",
			credentials: "include",
			headers: {
				accept: "application/json",
				"content-type": "application/json",
			},
			body: JSON.stringify({ reason: args.reason }),
		},
	);
	return parseOrThrow<RejectResponse>(res);
}

/**
 * Required by the scaffold api shape. No server route supports DELETE; we
 * expose a no-op stub here so that hook wiring stays consistent with other
 * crud-list features. The UI never calls this.
 */
export async function remove(_id: string): Promise<{ removed: boolean }> {
	return { removed: false };
}

/**
 * Same rationale as `remove` — scaffold expects `create`, but applicants
 * are seeded from the signup flow, not from the review UI. Stub present for
 * type compatibility only.
 */
export async function create(_body: Record<string, unknown>): Promise<AffiliateApprovalRow> {
	throw new Error(
		"affiliate-approval is read-only from the review UI; applicants are created via the signup flow.",
	);
}

export interface CreateAffiliateApprovalBody {
	[key: string]: unknown;
}

export const affiliateApprovalQueryKeys = {
	all: ["affiliate-approval"] as const,
	list: () => [...affiliateApprovalQueryKeys.all] as const,
	queue: (q: ListQuery) => [...affiliateApprovalQueryKeys.all, "queue", q] as const,
	detail: (id: string) => [...affiliateApprovalQueryKeys.all, id] as const,
};

// ---------------------------------------------------------------------------
// Pure helpers (unit-testable without the DOM).
// ---------------------------------------------------------------------------

export function formatSignupDate(unix: number): string {
	if (!Number.isFinite(unix) || unix <= 0) return "—";
	const d = new Date(unix * 1000);
	const yyyy = d.getUTCFullYear();
	const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
	const dd = String(d.getUTCDate()).padStart(2, "0");
	return `${yyyy}-${mm}-${dd}`;
}

export function formatInteger(n: number): string {
	if (!Number.isFinite(n)) return "0";
	return new Intl.NumberFormat("en-US").format(Math.trunc(n));
}

export interface QueueStats {
	total: number;
	pending: number;
	approved: number;
	rejected: number;
}

export function computeQueueStats(rows: readonly AffiliateApprovalRow[]): QueueStats {
	let pending = 0;
	let approved = 0;
	let rejected = 0;
	for (const r of rows) {
		if (r.status === "pending") pending++;
		else if (r.status === "approved") approved++;
		else if (r.status === "rejected") rejected++;
	}
	return {
		total: rows.length,
		pending,
		approved,
		rejected,
	};
}
